import React from 'react';
import PageHero from '../components/PageHero';
import { Helmet } from 'react-helmet-async';
import CTASection from '../components/CTASection';
import { SERVICE_SLEEP_ADULT } from '../assets/images';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Sleep from '../assets/Hero/Sleep.svg';
import AdultsSignsCarousel from '../components/Adultssignscarousel';


const { FiAlertCircle, FiHeart, FiSettings, FiCheckCircle } = FiIcons;

export default function SleepApneaAdults() {
  const symptoms = [
    "Loud, chronic snoring",
    "Waking up gasping or choking",
    "Morning headaches",
    "Excessive daytime sleepiness",
    "Difficulty concentrating",
    "Dry mouth upon waking"
  ];

  return (
    <div>

      {/* ✅ Helmet Added */}
      <Helmet>
        <title>Sleep Apnea Treatment for Adults | MARPE, DOME & Oral Appliance</title>
        <meta
          name="description"
          content="Advanced sleep apnea treatment for adults including MARPE, DOME, and oral appliance therapy. Treat snoring, breathing issues, and improve sleep quality."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalCondition",
            "name": "Sleep Apnea",
            "alternateName": "Obstructive Sleep Apnea",
            "description": "A sleep disorder where breathing repeatedly stops and starts during sleep.",
            "possibleTreatment": [
              {
                "@type": "MedicalTherapy",
                "name": "Oral Appliance Therapy"
              },
              {
                "@type": "MedicalTherapy",
                "name": "Myofunctional Therapy"
              },
              {
                "@type": "MedicalProcedure",
                "name": "MARPE"
              },
              {
                "@type": "MedicalProcedure",
                "name": "DOME"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <PageHero
        title="Sleep Apnea in Adults"
        subtitle="Treat the root cause of sleep apnea with advanced airway-focused solutions including MARPE and DOME."
        image={Sleep}
      />

      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          {/* Symptoms */}
          <div>
            <div className="flex items-center gap-4 mb-8">
              <SafeIcon icon={FiAlertCircle} className="text-4xl text-accent2" />
              <h2 className="text-3xl font-bold text-secondary">Common Symptoms of Sleep Apnea</h2>
            </div>
            <p className="text-lg text-gray-600 mb-8">
              Obstructive Sleep Apnea (OSA) is often overlooked in adults. These symptoms usually indicate a narrowed airway and poor breathing during sleep:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {symptoms.map((sym, i) => (
                <div key={i} className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} className="text-primary mt-1 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{sym}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Risks */}
          <div>
            <div className="flex items-center gap-4 mb-8">
              <SafeIcon icon={FiHeart} className="text-4xl text-accent2" />
              <h2 className="text-3xl font-bold text-secondary">Why Treatment is Critical</h2>
            </div>
            <p className="text-lg text-gray-600 mb-6">
              Sleep apnea is not just snoring. It is a serious condition linked to long-term health risks due to repeated oxygen deprivation:
            </p>
            <ul className="space-y-4 text-lg text-gray-700">
              <li className="flex items-center gap-3"><span className="w-2 h-2 bg-primary rounded-full"></span> High blood pressure</li>
              <li className="flex items-center gap-3"><span className="w-2 h-2 bg-primary rounded-full"></span> Heart disease & stroke</li>
              <li className="flex items-center gap-3"><span className="w-2 h-2 bg-primary rounded-full"></span> Type 2 diabetes</li>
              <li className="flex items-center gap-3"><span className="w-2 h-2 bg-primary rounded-full"></span> Weight gain & fatigue</li>
            </ul>
          </div>
        </div>
      </section>

      <AdultsSignsCarousel />

      {/* Treatments */}
      <section className="py-8 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-16 text-center">
            Treatment Options for Adult Sleep Apnea
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            <div className="bg-white p-10 rounded-xl shadow-soft">
              <SafeIcon icon={FiSettings} className="text-4xl text-primary mb-6" />
              <h3 className="text-2xl font-bold text-secondary mb-4">
                Oral Appliance Therapy
              </h3>
              <p className="text-gray-600 text-lg">
                Custom-designed oral appliances gently reposition the lower jaw forward during sleep. This helps maintain an open airway, reducing snoring and apnea episodes. Ideal for mild to moderate cases and patients who cannot tolerate CPAP.
              </p>
            </div>

            <div className="bg-white p-10 rounded-xl shadow-soft">
              <SafeIcon icon={FiHeart} className="text-4xl text-primary mb-6" />
              <h3 className="text-2xl font-bold text-secondary mb-4">
                Myofunctional Therapy
              </h3>
              <p className="text-gray-600 text-lg">
                Targeted exercises strengthen the tongue and airway muscles, helping improve breathing patterns. Often combined with other treatments for long-term stability.
              </p>
            </div>

            <div className="bg-white p-10 rounded-xl shadow-soft">
              <SafeIcon icon={FiCheckCircle} className="text-4xl text-primary mb-6" />
              <h3 className="text-2xl font-bold text-secondary mb-4">
                MARPE (Non-Surgical Jaw Expansion)
              </h3>
              <p className="text-gray-600 text-lg">
                MARPE (Mini-implant Assisted Rapid Palatal Expansion) is a non-surgical technique used to widen the upper jaw in adults. By expanding the airway at its source, it can significantly improve breathing and reduce sleep apnea symptoms.
              </p>
            </div>

            <div className="bg-white p-10 rounded-xl shadow-soft">
              <SafeIcon icon={FiCheckCircle} className="text-4xl text-primary mb-6" />
              <h3 className="text-2xl font-bold text-secondary mb-4">
                DOME (Surgical Airway Expansion)
              </h3>
              <p className="text-gray-600 text-lg">
                DOME (Distraction Osteogenesis Maxillary Expansion) is a surgical solution for adults with severe airway restriction. It provides predictable expansion and is often recommended in advanced sleep apnea or when non-surgical options are insufficient.
              </p>
            </div>

          </div>

          {/* Added SEO Section */}
          <div className="max-w-4xl mx-auto mt-16 text-center">
            <h3 className="text-2xl font-bold text-secondary mb-4">
              MARPE vs DOME – Which is Right for You?
            </h3>
            <p className="text-gray-600 text-lg">
              The choice between MARPE and DOME depends on your age, airway anatomy, and severity of sleep apnea. While MARPE offers a non-surgical approach for moderate cases, DOME is recommended for severe skeletal restriction where maximum airway expansion is required.
            </p>
          </div>

        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-6 bg-secondary text-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Why Choose Us for Sleep Apnea Treatment?
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed mb-10">
            We focus on treating the root cause of sleep apnea, not just the symptoms. Using advanced airway analysis, 3D imaging, and treatments like MARPE and DOME, we deliver long-term breathing improvement and better sleep quality.
          </p>
          <div className="flex justify-center gap-8 text-accent2">
            <SafeIcon icon={FiCheckCircle} className="text-4xl" />
            <SafeIcon icon={FiCheckCircle} className="text-4xl" />
            <SafeIcon icon={FiCheckCircle} className="text-4xl" />
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}